"use client";

import { ArrowRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function SignatureSpiritsConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="flex gap-6 overflow-x-auto pb-4">
        {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="min-w-[280px] md:min-w-[320px] aspect-[3/4] rounded-lg bg-muted/30 animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="text-sm text-muted-foreground">
        Unable to load the inventory.{" "}
        <Button variant="link" onClick={refetch} className="text-primary p-0 h-auto">
          Retry
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <p className="text-muted-foreground italic font-default">
        Our inventory is currently being updated. Please check back shortly.
      </p>;
  }
  return <div className="relative -mx-4 md:-mx-8">
      <div className="overflow-x-auto scrollbar-thin pb-6 px-4 md:px-8">
        <div className="flex gap-6 md:gap-8 min-w-max">
          {products.map((product, i) => <article data-index={i} key={product.id} className="group relative w-[280px] md:w-[340px] flex-shrink-0 bg-card transition-all duration-500 hover:-translate-y-1.5 hover:shadow-lg" style={{
          boxShadow: "0 10px 30px rgba(0,0,0,0.02)"
        }}>
              {}
              <span className="absolute left-0 top-0 bottom-0 w-px bg-primary/0 group-hover:bg-primary transition-colors duration-700" aria-hidden="true" />

              {}
              <span className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700" style={{
            boxShadow: "inset 0 0 80px hsl(210 100% 50% / 0.05)"
          }} aria-hidden="true" />

              <Link to={product.link} className="block">
                {}
                <div className="relative aspect-[3/4] overflow-hidden bg-muted">
                  {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-[1200ms] ease-out group-hover:scale-105" /> : <div className="absolute inset-0 flex items-center justify-center text-muted-foreground/40 font-default italic">
                      No image
                    </div>}
                </div>
              </Link>

              {}
              <div className="px-6">
                <div className="h-px bg-border" aria-hidden="true" />
              </div>

              {}
              <div className="px-6 py-5">
                <Link to={product.link} className="block group/title">
                  <h3 className="font-default text-2xl md:text-[1.625rem] leading-tight text-foreground group-hover/title:text-primary transition-colors duration-300">
                    {product.title}
                  </h3>
                </Link>

                {product.categories && product.categories.length > 0 && <p className="mt-1 font-default text-sm text-muted-foreground tracking-wide">
                    {product.categories[0].name}
                  </p>}

                <div className="mt-5 flex items-end justify-between gap-3">
                  <div className="font-default text-primary text-xl tracking-wide">
                    ${Number(product.price).toFixed(2)}
                  </div>

                  <AddToCartButton productId={product.id} variant="outline" size="icon" text="" showQuantity={false} className="w-auto" buttonClassName="bg-primary text-primary-foreground h-10 w-10 rounded-lg border border-primary hover:bg-primary/90 transition-all duration-300 [&>svg]:hidden" />
                </div>

                {}
                <div className="pointer-events-none absolute bottom-7 right-6 flex items-center justify-center">
                  <Plus className="h-4 w-4 text-primary transition-colors duration-300" />
                </div>
              </div>
            </article>)}
        </div>
      </div>
    </div>;
}
export default function TrendingTechDeals() {
  const wp_query_params = {
    per_page: 5,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="4471" id="trending-tech-deals" className="bg-background text-foreground relative overflow-hidden py-20 md:py-32">
      <div className="relative max-w-7xl mx-auto px-4 md:px-8">
        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-20">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-primary" aria-hidden="true" />
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-primary">
                03 — Trending Tech
              </span>
            </div>

            <h2 className="font-default text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-foreground">
              Precision-engineered
              <br />
              <em className="italic text-primary font-light">
                mobile hardware.
              </em>
            </h2>
          </div>

          <div className="lg:col-span-5 lg:pt-8">
            <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground">
              Discover our latest selection of flagship smartphones and premium accessories. Every device is verified for performance, ensuring you stay connected with the best technology available.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <span className="h-px w-12 bg-border" aria-hidden="true" />
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                TechWire Mobile
              </span>
            </div>
          </div>
        </div>

        {}
        <EcommerceProductsProvider wp_query={{
        per_page: 5,
        orderby: "date",
        order: "desc"
      }}>
          <SignatureSpiritsConsumer />
        </EcommerceProductsProvider>

        {}
        <div className="mt-16 md:mt-20 flex justify-center">
          <Link to="/ShopPage" className="group inline-flex items-center gap-3">
            <span className="h-px w-8 bg-primary/60 group-hover:w-16 group-hover:bg-primary transition-all duration-500" aria-hidden="true" />
            <span className="font-default text-lg md:text-xl text-primary group-hover:text-primary/80 transition-colors duration-300 tracking-wide">
              View Full Inventory
            </span>
            <ArrowRight className="h-4 w-4 text-primary group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </div>
    </section>;
}